import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, LoadingState, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, PencilSimple, Archive, CheckCircle, CalendarBlank,
    Paperclip, DownloadSimple, Globe, Buildings, HouseLine, UsersThree, WarningCircle,
    X, FilePdf, FileDoc, Image as ImageIcon, ChartBar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const PRIORITY_VARIANT = { normal: "neutral", important: "warn", urgent: "danger" };
const STATUS_VARIANT = { draft: "neutral", published: "success", archived: "default" };
const AUDIENCE_ICON = { ALL: Globe, BLOCK: Buildings, FLAT: HouseLine, COMMITTEE_ADMIN: UsersThree };
const AUDIENCE_LABEL_BASE = { ALL: "Everyone", BLOCK: "Block", FLAT: "Flat", COMMITTEE_ADMIN: "Committee & Admin" };
const ALLOWED_ATTACHMENT_MIMES = ["image/jpeg", "image/png", "image/webp", "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_MB = 10;

const statusLabel = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());

function fmt(dt) {
    if (!dt) return "—";
    try { return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return dt; }
}
function toInputDT(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const off = d.getTimezoneOffset();
        return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
    } catch { return ""; }
}
function fromInputDT(local) {
    if (!local) return null;
    try { return new Date(local).toISOString(); } catch { return null; }
}
function attachmentIcon(ct) {
    if ((ct || "").includes("pdf")) return FilePdf;
    if ((ct || "").includes("word") || (ct || "").includes("doc")) return FileDoc;
    return ImageIcon;
}
function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// ---------- Attachment row (secure blob fetch on download) ----------
function AttachmentRow({ noticeId, att }) {
    const [busy, setBusy] = useState(false);
    const Icon = attachmentIcon(att.content_type);
    async function download() {
        setBusy(true);
        try {
            const res = await api.get(`/announcements/${noticeId}/attachments/${att.file_id}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = att.file_name || "attachment"; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="flex items-center justify-between bg-brand-bg border border-brand-line rounded-sm px-3 py-2" data-testid={`notice-attachment-${att.file_id}`}>
            <div className="flex items-center gap-2 min-w-0">
                <Icon size={16} weight="duotone" className="text-brand-inkSoft shrink-0" />
                <span className="text-sm text-brand-ink truncate">{att.file_name}</span>
                <span className="text-[10px] text-brand-inkSoft shrink-0">{fmtSize(att.size)}</span>
            </div>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={download} data-testid={`notice-attachment-download-${att.file_id}`} className="text-brand-action shrink-0">
                <DownloadSimple size={14} className="mr-1" /> {busy ? "..." : "Download"}
            </Button>
        </div>
    );
}

function audienceLabel(aud, flats) {
    if (!aud) return "Everyone";
    if (aud.type === "BLOCK") return `Block ${aud.block}`;
    if (aud.type === "FLAT") {
        const f = flats.find(x => x.id === aud.flat_id);
        return f ? `Flat ${f.block}-${f.number}` : "Specific Flat";
    }
    return AUDIENCE_LABEL_BASE[aud.type] || "Everyone";
}

// ---------- Detail Dialog ----------
function NoticeDetail({ notice, isStaff, flats, onClose, onChange, onEdit }) {
    const [detail, setDetail] = useState(notice);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const d = await api.get(`/announcements/${notice.id}`);
            setDetail(d.data);
            if (isStaff) {
                try { const s = await api.get(`/announcements/${notice.id}/stats`); setStats(s.data); } catch { /* ignore */ }
            }
        } catch (e) { toast.error(formatError(e)); onClose?.(); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [notice.id]);

    async function doPublish() {
        try { await api.post(`/announcements/${notice.id}/publish`); toast.success("Notice published"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function doArchive() {
        if (!window.confirm("Archive this notice? Residents will no longer see it.")) return;
        try { await api.post(`/announcements/${notice.id}/archive`); toast.success("Notice archived"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }

    if (loading) return <LoadingState message="Loading notice..." />;
    const AudIcon = AUDIENCE_ICON[detail.audience?.type] || Globe;
    const isImportant = detail.priority === "important" || detail.priority === "urgent";

    return (
        <div className="space-y-5" data-testid="notice-detail-dialog">
            {isImportant && (
                <div className={`rounded-sm px-3 py-2 text-xs font-medium flex items-center gap-1.5 ${detail.priority === "urgent" ? "bg-[#FEF2F2] text-[#DC2626]" : "bg-[#FFF7ED] text-[#EA580C]"}`} data-testid="notice-priority-banner">
                    <WarningCircle size={14} weight="fill" /> {detail.priority === "urgent" ? "Urgent Notice" : "Important Notice"}
                </div>
            )}
            <div className="border-b border-brand-line pb-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <h2 className="font-heading text-xl md:text-2xl tracking-tight text-brand-ink" data-testid="notice-detail-title">{detail.title}</h2>
                    <div className="flex flex-wrap items-center gap-2">
                        {isStaff && <Chip variant={STATUS_VARIANT[detail.status]} data-testid="notice-detail-status">{statusLabel(detail.status)}</Chip>}
                        <Chip variant={PRIORITY_VARIANT[detail.priority]} data-testid="notice-detail-priority">{statusLabel(detail.priority)}</Chip>
                        <Chip data-testid="notice-detail-category">{statusLabel(detail.category)}</Chip>
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-brand-inkSoft">
                    <span className="flex items-center gap-1"><CalendarBlank size={12} weight="duotone" /> Published {fmt(detail.published_at)}</span>
                    {detail.expires_at && <span className="flex items-center gap-1"><CalendarBlank size={12} weight="duotone" /> Expires {fmt(detail.expires_at)}</span>}
                    <span className="flex items-center gap-1" data-testid="notice-detail-audience"><AudIcon size={12} weight="duotone" /> {audienceLabel(detail.audience, flats)}</span>
                </div>
            </div>

            <div className="bg-brand-bg border border-brand-line rounded-sm p-4">
                <p className="text-sm text-brand-ink whitespace-pre-wrap" data-testid="notice-detail-content">{detail.content}</p>
            </div>

            {detail.attachments?.length > 0 && (
                <div className="space-y-2" data-testid="notice-detail-attachments">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft flex items-center gap-1"><Paperclip size={12} weight="duotone" /> Attachments ({detail.attachments.length})</p>
                    {detail.attachments.map(a => <AttachmentRow key={a.id} noticeId={detail.id} att={a} />)}
                </div>
            )}

            {isStaff && stats && (
                <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="notice-read-stats">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1"><ChartBar size={12} weight="duotone" /> Read Status</p>
                    <div className="grid grid-cols-4 gap-3 text-center">
                        <div><p className="font-heading text-xl text-brand-ink">{stats.total_eligible}</p><p className="text-[10px] text-brand-inkSoft uppercase tracking-overline">Eligible</p></div>
                        <div><p className="font-heading text-xl text-green-700">{stats.read_count}</p><p className="text-[10px] text-brand-inkSoft uppercase tracking-overline">Read</p></div>
                        <div><p className="font-heading text-xl text-brand-action">{stats.unread_count}</p><p className="text-[10px] text-brand-inkSoft uppercase tracking-overline">Unread</p></div>
                        <div><p className="font-heading text-xl text-brand-ink">{stats.read_percentage}%</p><p className="text-[10px] text-brand-inkSoft uppercase tracking-overline">Read Rate</p></div>
                    </div>
                </div>
            )}

            {!isStaff && (
                <p className="text-xs text-brand-inkSoft" data-testid="notice-detail-createdby">Posted by {detail.created_by_name}</p>
            )}

            {isStaff && detail.status !== "archived" && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-brand-line">
                    <Button variant="outline" size="sm" onClick={() => onEdit?.(detail)} data-testid="notice-edit-btn" className="rounded-full border-brand-line">
                        <PencilSimple size={14} className="mr-1" /> Edit
                    </Button>
                    {detail.status === "draft" && (
                        <Button size="sm" onClick={doPublish} data-testid="notice-publish-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                            <CheckCircle size={14} className="mr-1" /> Publish
                        </Button>
                    )}
                    {detail.status === "published" && (
                        <Button variant="outline" size="sm" onClick={doArchive} data-testid="notice-archive-btn" className="rounded-full border-brand-line text-brand-inkSoft">
                            <Archive size={14} className="mr-1" /> Archive
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Create/Edit Dialog ----------
function NoticeFormDialog({ open, onOpenChange, editing, meta, flats, onSaved }) {
    const emptyForm = () => ({
        title: "", content: "", category: "general", priority: "normal",
        publish_at: "", expires_at: "", audienceType: "ALL", block: "", flat_id: "",
    });
    const [form, setForm] = useState(emptyForm());
    const [pendingFiles, setPendingFiles] = useState([]); // for create-mode staging: [{file, name, uploading, file_id}]
    const [saving, setSaving] = useState(false);
    const isEdit = !!editing;

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                title: editing.title, content: editing.content, category: editing.category, priority: editing.priority,
                publish_at: toInputDT(editing.publish_at), expires_at: toInputDT(editing.expires_at),
                audienceType: editing.audience?.type || "ALL", block: editing.audience?.block || "", flat_id: editing.audience?.flat_id || "",
            });
        } else {
            setForm(emptyForm());
        }
        setPendingFiles([]);
    }, [open, editing]);

    const blocksList = useMemo(() => Array.from(new Set(flats.map(f => f.block).filter(Boolean))).sort(), [flats]);
    const flatsForBlock = useMemo(() => flats.filter(f => f.block === form.block).sort((a, b) => (a.number || "").localeCompare(b.number || "")), [flats, form.block]);

    function buildAudience() {
        if (form.audienceType === "ALL" || form.audienceType === "COMMITTEE_ADMIN") return { type: form.audienceType };
        if (form.audienceType === "BLOCK") return { type: "BLOCK", block: form.block };
        return { type: "FLAT", flat_id: form.flat_id };
    }

    async function handleFilePick(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        const remaining = MAX_ATTACHMENTS - (isEdit ? (editing.attachments?.length || 0) + pendingFiles.length : pendingFiles.length);
        if (remaining <= 0) { toast.error(`Maximum ${MAX_ATTACHMENTS} attachments`); return; }
        const toAdd = files.slice(0, remaining).filter(f => {
            if (!ALLOWED_ATTACHMENT_MIMES.includes(f.type)) { toast.error(`${f.name}: unsupported file type`); return false; }
            if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) { toast.error(`${f.name}: exceeds ${MAX_ATTACHMENT_MB}MB limit`); return false; }
            return true;
        });
        if (isEdit) {
            // notice already exists -> upload straight to it
            for (const f of toAdd) {
                try {
                    const fd = new FormData(); fd.append("file", f);
                    await api.post(`/announcements/${editing.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                } catch (err) { toast.error(`${f.name}: ${formatError(err)}`); }
            }
            onSaved?.();
        } else {
            const seeds = toAdd.map(f => ({ file: f, name: f.name, uploading: true, file_id: null }));
            setPendingFiles(prev => [...prev, ...seeds]);
            await Promise.all(seeds.map(async (seed) => {
                try {
                    const fd = new FormData(); fd.append("file", seed.file);
                    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setPendingFiles(prev => prev.map(p => p === seed ? { ...p, uploading: false, file_id: data.id } : p));
                } catch (err) {
                    toast.error(`${seed.file.name}: upload failed`);
                    setPendingFiles(prev => prev.filter(p => p !== seed));
                }
            }));
        }
    }

    function validate() {
        if (!form.title.trim()) { toast.error("Title cannot be empty"); return false; }
        if (!form.content.trim()) { toast.error("Content cannot be empty"); return false; }
        if (form.audienceType === "BLOCK" && !form.block) { toast.error("Please select a block"); return false; }
        if (form.audienceType === "FLAT" && !form.flat_id) { toast.error("Please select a flat"); return false; }
        if (form.expires_at && form.publish_at && new Date(form.expires_at) <= new Date(form.publish_at)) { toast.error("Expiry cannot be before the publish date/time"); return false; }
        return true;
    }

    async function save(publishNow) {
        if (!validate()) return;
        if (pendingFiles.some(p => p.uploading)) { toast.error("Please wait — attachments are still uploading"); return; }
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(), content: form.content.trim(), category: form.category, priority: form.priority,
                publish_at: fromInputDT(form.publish_at), expires_at: fromInputDT(form.expires_at), audience: buildAudience(),
            };
            if (isEdit) {
                await api.patch(`/announcements/${editing.id}`, payload);
                if (publishNow && editing.status === "draft") await api.post(`/announcements/${editing.id}/publish`);
                toast.success("Notice updated");
            } else {
                payload.status = publishNow ? "published" : "draft";
                const fileIds = pendingFiles.map(p => p.file_id).filter(Boolean);
                if (fileIds.length) payload.attachment_file_ids = fileIds;
                await api.post("/announcements", payload);
                toast.success(publishNow ? "Notice published" : "Draft saved");
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">{isEdit ? "Edit Notice" : "Create Notice"}</DialogTitle>
                    <DialogDescription>{isEdit ? "Update details. Publishing status changes are handled separately." : "Draft it first, or publish immediately."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>Title *</Label>
                        <Input data-testid="notice-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. AGM 2026 — Save the Date" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Content *</Label>
                        <Textarea data-testid="notice-content-input" rows={5} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="rounded-sm border-brand-line" placeholder="Write the notice body..." />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Category</Label>
                            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="notice-category-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{meta.categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Priority</Label>
                            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                                <SelectTrigger data-testid="notice-priority-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{meta.priorities.map(p => <SelectItem key={p} value={p}>{statusLabel(p)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Publish Date/Time (optional)</Label>
                            <Input data-testid="notice-publish-at-input" type="datetime-local" value={form.publish_at} onChange={e => setForm({ ...form, publish_at: e.target.value })} className="rounded-sm border-brand-line" />
                            <p className="text-[10px] text-brand-inkSoft">Leave blank to publish immediately when you hit Publish</p>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Expiry Date/Time (optional)</Label>
                            <Input data-testid="notice-expires-at-input" type="datetime-local" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="space-y-2 border border-brand-line rounded-sm p-3 bg-brand-bg" data-testid="notice-audience-picker">
                        <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Target Audience</Label>
                        <Select value={form.audienceType} onValueChange={v => setForm({ ...form, audienceType: v, block: "", flat_id: "" })}>
                            <SelectTrigger data-testid="notice-audience-type-select" className="rounded-sm border-brand-line bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALL">Everyone</SelectItem>
                                <SelectItem value="BLOCK">Specific Block</SelectItem>
                                <SelectItem value="FLAT">Specific Flat</SelectItem>
                                <SelectItem value="COMMITTEE_ADMIN">Committee & Admin</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.audienceType === "BLOCK" && (
                            <Select value={form.block} onValueChange={v => setForm({ ...form, block: v })}>
                                <SelectTrigger data-testid="notice-audience-block-select" className="rounded-sm border-brand-line bg-white"><SelectValue placeholder="Pick block" /></SelectTrigger>
                                <SelectContent>{blocksList.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                            </Select>
                        )}
                        {form.audienceType === "FLAT" && (
                            <div className="grid grid-cols-2 gap-2">
                                <Select value={form.block} onValueChange={v => setForm({ ...form, block: v, flat_id: "" })}>
                                    <SelectTrigger data-testid="notice-audience-flat-block-select" className="rounded-sm border-brand-line bg-white"><SelectValue placeholder="Block" /></SelectTrigger>
                                    <SelectContent>{blocksList.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                                </Select>
                                <Select value={form.flat_id} onValueChange={v => setForm({ ...form, flat_id: v })}>
                                    <SelectTrigger data-testid="notice-audience-flat-select" className="rounded-sm border-brand-line bg-white"><SelectValue placeholder={form.block ? "Pick flat" : "Pick block first"} /></SelectTrigger>
                                    <SelectContent>{flatsForBlock.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <div className="space-y-2" data-testid="notice-attachments-picker">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-1"><Paperclip size={14} weight="duotone" /> Attachments</Label>
                            <span className="text-[10px] text-brand-inkSoft">JPG/PNG/WEBP/PDF/DOC · up to {MAX_ATTACHMENTS} · max {MAX_ATTACHMENT_MB}MB each</span>
                        </div>
                        {isEdit && editing.attachments?.length > 0 && (
                            <div className="space-y-1.5">{editing.attachments.map(a => <AttachmentRow key={a.id} noticeId={editing.id} att={a} />)}</div>
                        )}
                        {pendingFiles.map((p, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-brand-bg border border-brand-line rounded-sm px-3 py-2">
                                <span className="text-sm text-brand-ink truncate">{p.name}{p.uploading ? " (uploading...)" : ""}</span>
                                <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))} className="text-brand-inkSoft hover:text-brand-action"><X size={14} /></button>
                            </div>
                        ))}
                        <label className="inline-flex items-center gap-1.5 text-xs text-brand-action cursor-pointer" data-testid="notice-attachment-upload-label">
                            <Plus size={14} /> Add attachment
                            <input type="file" className="hidden" accept={ALLOWED_ATTACHMENT_MIMES.join(",")} onChange={handleFilePick} data-testid="notice-attachment-file-input" />
                        </label>
                    </div>
                </div>
                <DialogFooter className="flex-wrap gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-brand-line">Cancel</Button>
                    <Button data-testid="notice-save-draft-btn" variant="outline" disabled={saving} onClick={() => save(false)} className="rounded-full border-brand-line text-brand-ink">
                        Save {isEdit && editing.status !== "draft" ? "Changes" : "as Draft"}
                    </Button>
                    {(!isEdit || editing.status === "draft") && (
                        <Button data-testid="notice-publish-submit-btn" disabled={saving} onClick={() => save(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                            <CheckCircle size={14} className="mr-1" /> Publish
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Resident Notice Card ----------
function NoticeCard({ notice, onOpen }) {
    const isImportant = notice.priority === "important" || notice.priority === "urgent";
    return (
        <div onClick={() => onOpen(notice)} data-testid={`notice-card-${notice.id}`}
             className={`bg-white border rounded-sm p-5 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200 ${notice.priority === "urgent" ? "border-l-4 border-l-[#DC2626] border-brand-line" : notice.priority === "important" ? "border-l-4 border-l-[#EA580C] border-brand-line" : "border-brand-line"}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    {!notice.is_read && <span className="w-2 h-2 rounded-full bg-brand-action shrink-0" data-testid={`notice-unread-dot-${notice.id}`} />}
                    <h3 className="font-heading text-lg tracking-tight text-brand-ink">{notice.title}</h3>
                </div>
                {isImportant && <Chip variant={PRIORITY_VARIANT[notice.priority]}>{statusLabel(notice.priority)}</Chip>}
            </div>
            <p className="text-sm text-brand-inkSoft mt-2 line-clamp-2">{notice.content}</p>
            <div className="flex items-center gap-3 mt-3 text-[10px] text-brand-inkSoft uppercase tracking-overline">
                <Chip>{statusLabel(notice.category)}</Chip>
                <span>{fmt(notice.published_at)}</span>
                {notice.attachments?.length > 0 && <span className="flex items-center gap-1 normal-case"><Paperclip size={11} weight="duotone" /> {notice.attachments.length}</span>}
            </div>
        </div>
    );
}

// ---------- Main Page ----------
export default function Announcements() {
    const { user } = useAuth();
    const isStaff = user?.role === "admin" || user?.role === "committee";
    const [searchParams, setSearchParams] = useSearchParams();
    const [notices, setNotices] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [flats, setFlats] = useState([]);
    const [meta, setMeta] = useState({ categories: [], priorities: ["normal", "important", "urgent"], statuses: ["draft", "published", "archived"] });

    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [sortBy, setSortBy] = useState("newest");

    const [selected, setSelected] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    async function loadList() {
        setLoading(true);
        try {
            const params = { sort: sortBy };
            if (isStaff && statusFilter !== "all") params.status = statusFilter;
            if (categoryFilter !== "all") params.category = categoryFilter;
            if (priorityFilter !== "all") params.priority = priorityFilter;
            if (q.trim()) params.q = q.trim();
            const r = await api.get("/announcements", { params });
            setNotices(r.data);
            if (isStaff) { try { const s = await api.get("/announcements/summary"); setSummary(s.data); } catch { /* ignore */ } }
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [statusFilter, categoryFilter, priorityFilter, sortBy]);

    useEffect(() => {
        (async () => {
            try {
                const m = await api.get("/announcements/categories");
                setMeta(m.data);
            } catch { /* ignore */ }
            try { const f = await api.get("/flats"); setFlats(f.data || []); } catch { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff]);

    // Notice Notifications V2 deep-link: /app/announcements?id=<id>
    useEffect(() => {
        const openId = searchParams.get("id");
        if (openId) { setSelected({ id: openId }); setSearchParams({}, { replace: true }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function openCreate() { setEditing(null); setFormOpen(true); }
    function openEdit(notice) { setSelected(null); setEditing(notice); setFormOpen(true); }

    return (
        <div className="animate-fade-up space-y-6" data-testid="announcements-page">
            <PageHeader
                overline="Governance"
                title="Notices"
                description={isStaff ? "Publish and manage society-wide communications, targeted to the right audience." : "Stay up to date with announcements from your society."}
                actions={isStaff && (
                    <Button data-testid="notice-create-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                        <Plus size={16} className="mr-1.5" /> Create Notice
                    </Button>
                )}
            />

            {isStaff && summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="notice-summary">
                    <StatCard label="Total" value={summary.total} testId="notice-summary-total" />
                    <StatCard label="Published" value={summary.published} accent testId="notice-summary-published" />
                    <StatCard label="Drafts" value={summary.drafts} testId="notice-summary-drafts" />
                    <StatCard label="Expiring Soon" value={summary.expiring_soon} testId="notice-summary-expiring" />
                </div>
            )}

            <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="notice-filters">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-2 relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="notice-search" placeholder="Search title or content..." value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && loadList()} className="rounded-sm border-brand-line pl-9" />
                    </div>
                    {isStaff && (
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger data-testid="notice-status-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Status" /></SelectTrigger>
                            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                        </Select>
                    )}
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger data-testid="notice-category-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All categories</SelectItem>{meta.categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                        <SelectTrigger data-testid="notice-priority-filter" className="rounded-sm border-brand-line"><SelectValue placeholder="Priority" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All priorities</SelectItem>{meta.priorities.map(p => <SelectItem key={p} value={p}>{statusLabel(p)}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger data-testid="notice-sort" className="rounded-sm border-brand-line w-44"><SelectValue placeholder="Sort" /></SelectTrigger>
                        <SelectContent><SelectItem value="newest">Newest first</SelectItem><SelectItem value="oldest">Oldest first</SelectItem></SelectContent>
                    </Select>
                    <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={loadList} className="text-brand-ink"><MagnifyingGlass size={14} className="mr-1" /> Search</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setQ(""); setStatusFilter("all"); setCategoryFilter("all"); setPriorityFilter("all"); setSortBy("newest"); }} className="text-brand-inkSoft">Clear</Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <LoadingState message="Loading notices..." />
            ) : notices.length === 0 ? (
                <EmptyState title="No notices yet" description={isStaff ? "Create your first society-wide notice." : "Nothing posted for you yet — check back soon."} />
            ) : isStaff ? (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden" data-testid="notice-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[820px]">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Title</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Category</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Priority</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Status</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Audience</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Published</th>
                                    <th className="py-2 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">Expires</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notices.map(n => (
                                    <tr key={n.id} data-testid={`notice-row-${n.id}`} onClick={() => setSelected(n)} className="border-b border-brand-line/60 hover:bg-brand-bg/50 cursor-pointer">
                                        <td className="py-3 px-4 text-brand-ink font-medium">
                                            {n.title}
                                            {n.attachments?.length > 0 && <span className="ml-1.5 text-[10px] text-brand-inkSoft inline-flex items-center gap-0.5"><Paperclip size={10} weight="duotone" /> {n.attachments.length}</span>}
                                        </td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{statusLabel(n.category)}</td>
                                        <td className="py-3 px-4"><Chip variant={PRIORITY_VARIANT[n.priority]}>{statusLabel(n.priority)}</Chip></td>
                                        <td className="py-3 px-4"><Chip variant={STATUS_VARIANT[n.status]}>{statusLabel(n.status)}</Chip></td>
                                        <td className="py-3 px-4 text-brand-inkSoft text-xs">{audienceLabel(n.audience, flats)}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft text-xs">{n.status === "draft" ? "—" : fmt(n.published_at)}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft text-xs">{fmt(n.expires_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="notice-list">
                    {notices.map(n => <NoticeCard key={n.id} notice={n} onOpen={setSelected} />)}
                </div>
            )}

            <Dialog open={!!selected} onOpenChange={v => !v && setSelected(null)}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                    {selected && (
                        <NoticeDetail notice={selected} isStaff={isStaff} flats={flats} onClose={() => setSelected(null)} onChange={loadList} onEdit={openEdit} />
                    )}
                </DialogContent>
            </Dialog>

            <NoticeFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} meta={meta} flats={flats} onSaved={loadList} />
        </div>
    );
}
